import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DocumentIngestion_ExtractFieldsResult,
  Estate_CreateEstateInput,
  EstateType,
} from 'queries';

import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';

import { EstateDetailDocumentIngestionPrompt } from './estate-detail-document-ingestion-prompt';

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '18px',
      marginTop: '20px',
    },
    inputGrid: {
      paddingRight: '20px',
    },
    addButton: {
      backgroundColor: 'primary',
      borderRadius: '8px',
      marginTop: '20px',
    },
    copyBtnContainer: {
      marginTop: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    infoText: {
      opacity: 0.6,
    },
    grantorContainer: {
      padding: '20px 0',
      borderBottom: '2px solid #E5E5E5',
      '& .title': {
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        marginBottom: '10px',
        '& button': {
          backgroundColor: 'primary',
          borderRadius: '8px',
          marginLeft: '30px',
        },
      },
    },
  }),
);

export type GrantorTrustee = {
  grantorFirstName: string;
  grantorLastName: string;
  isPrimaryTrustee: boolean;
  sortOrder: number;
};

type EstateTrustAdminGrantorFormProps = {
  onCopyGrantorToTrustee: (grantor: GrantorTrustee) => void;
};

const EstateTrustAdminGrantorForm = ({
  onCopyGrantorToTrustee,
}: EstateTrustAdminGrantorFormProps) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<Estate_CreateEstateInput>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'estateTrustadminFields.estateGrantors',
  });

  const estateTrustees = watch('estateTrustadminFields.estateTrustees');
  const grantors = watch('estateTrustadminFields.estateGrantors');

  const primaryGrantorIndex = fields.findIndex((item) => item.isPrimaryGrantor);

  const errorMessage =
    errors.estateTrustadminFields?.estateGrantors?.message ||
    errors.estateTrustadminFields?.estateGrantors?.root?.message;

  const isGrantorATrustee = (grantor: { grantorFirstName?: string; grantorLastName?: string }) => {
    return estateTrustees?.some(
      (trustee) =>
        trustee.trusteeFirstName === grantor.grantorFirstName &&
        trustee.trusteeLastName === grantor.grantorLastName,
    );
  };

  const handleCopyGrantorToTrustee = (index: number) => {
    const currentGrantor = grantors?.[index];
    const estateHasPrimaryTrustee = estateTrustees?.some((trustee) => trustee.isPrimaryTrustee);
    const isPrimaryTrustee = !estateHasPrimaryTrustee && index === primaryGrantorIndex;

    if (currentGrantor) {
      onCopyGrantorToTrustee({
        grantorFirstName: currentGrantor.grantorFirstName,
        grantorLastName: currentGrantor.grantorLastName,
        isPrimaryTrustee,
        sortOrder: (grantors?.length || 0) + 1,
      });
    }
  };

  const handleExtractedFields = (
    index: number,
    extractedFields: DocumentIngestion_ExtractFieldsResult,
  ) => {
    update(index, {
      ...fields[index],
      grantorFirstName: extractedFields.firstName || '',
      grantorMiddleName: extractedFields.middleName || '',
      grantorLastName: extractedFields.lastName || '',
      grantorSsn: extractedFields.ssn || '',
    });
  };

  const renderGrantorFields = (item: { id: string }, index: number) => {
    const currentGrantor = grantors?.[index];
    const isTrustee = currentGrantor && isGrantorATrustee(currentGrantor);
    const isCopyToTrusteeVisible =
      !isTrustee && currentGrantor?.grantorFirstName && currentGrantor?.grantorLastName;

    return (
      <div key={item.id} className={classes.grantorContainer}>
        {index !== primaryGrantorIndex && (
          <>
            <div className="title">
              <div>Grantor 2</div>
              <Button variant="contained" type="button" onClick={() => remove(index)}>
                Remove
              </Button>
            </div>
            <EstateDetailDocumentIngestionPrompt
              portalId={portalId}
              onExtractFields={(values) => handleExtractedFields(index, values)}
              estateType={EstateType.Trustadmin}
            />
          </>
        )}

        <Grid container>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorFirstName`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Grantor First Name"
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorFirstName
                      ?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorMiddleName`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Grantor Middle Name or Initial"
                  helperText="*Optional. Please leave blank if no middle name."
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorMiddleName
                      ?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorLastName`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  label="Grantor Last Name"
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorLastName?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid className={classes.inputGrid} item xs={12} sm={6}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorSsn`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSsnField
                  {...field}
                  inputRef={ref}
                  label="Grantor SSN"
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorSsn?.message
                  }
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sm={6} className={classes.inputGrid}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.isGrantorStillAlive`}
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  label="Is Grantor Still Alive?"
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(e) => onChange(e.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </Grid>
        </Grid>
        {isCopyToTrusteeVisible && (
          <div className={classes.copyBtnContainer}>
            <Button
              variant="contained"
              type="button"
              onClick={() => handleCopyGrantorToTrustee(index)}
            >
              Copy to trustees
            </Button>
            <Typography className={classes.infoText}>
              Use the copy button for grantors also serving as trustees.
            </Typography>
          </div>
        )}

        {index === primaryGrantorIndex && (
          <Grid container>
            <Typography className={classes.title}>
              Grantor’s Last Address Street, City, State, Zip
            </Typography>
            <Grid container style={{ display: 'inline' }}>
              <Grid style={{ display: 'inline' }} className={classes.inputGrid} item xs={12} sm={6}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressStreet`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} label="Grantor Address Street" />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid className={classes.inputGrid} item xs={12} sm={6}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressCity`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} label="Grantor Address City" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressState`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} label="Grantor Address State" />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid className={classes.inputGrid} item xs={12} sm={6}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressZip`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} label="Grantor Address Zip" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>
    );
  };

  const renderNonPrimaryGrantorFields = (
    item: { id: string; isPrimaryGrantor: boolean },
    index: number,
  ) => {
    if (item.isPrimaryGrantor) {
      return null;
    }
    return renderGrantorFields(item, index);
  };

  return (
    <>
      {primaryGrantorIndex > -1 &&
        renderGrantorFields(fields[primaryGrantorIndex], primaryGrantorIndex)}
      <div>{errorMessage && <FormInputErrorMessage error={errorMessage} />}</div>
      {fields.map((item, index) => renderNonPrimaryGrantorFields(item, index))}
      {fields.length < 2 && (
        <Button
          variant="contained"
          type="button"
          className={classes.addButton}
          onClick={() =>
            append({
              grantorFirstName: '',
              grantorLastName: '',
              grantorSsn: '',
              isGrantorStillAlive: false,
              isPrimaryGrantor: false,
              sortOrder: fields.length + 1,
            })
          }
        >
          <AddIcon /> Add Grantor
        </Button>
      )}
    </>
  );
};

export default EstateTrustAdminGrantorForm;
